<script setup>
import { useCookie } from "@/@core/composable/useCookie";

definePage({
  name: "apps-user-importaciones",
});

const user = useCookie("userData");
const router = useRouter();

const empresas = ref([]);
const selectedEmpresa = ref(null);
const numeroCuenta = ref("");
const anyo = ref(null);

const itemsPerPage = ref(15);
const page = ref(1);
const importaciones = ref([]);
const totalImportaciones = ref(0);
const loading = ref(false);

const detailDialog = ref(false);
const selectedImportacion = ref(null);
const lineas = ref([]);
const totalLineas = ref(0);
const lineasPage = ref(1);
const lineasPerPage = ref(25);
const loadingLineas = ref(false);

const headers = [
  { title: "Fecha", key: "created_at" },
  { title: "Usuario", key: "usuario.name" },
  { title: "Año", key: "anyo" },
  { title: "Tipo", key: "tipo" },
  { title: "Columna", key: "columna_total" },
  { title: "Archivo", key: "archivo_nombre" },
  { title: "Líneas", key: "lineas_registradas" },
  { title: "Acciones", key: "actions", sortable: false },
];

const lineasHeaders = [
  { title: "Fila Excel", key: "fila_excel" },
  { title: "Cuenta", key: "numero_cuenta" },
  { title: "Campo", key: "campo" },
  { title: "Importe Excel", key: "importe_excel" },
  { title: "Anterior", key: "valor_anterior" },
  { title: "Nuevo", key: "valor_nuevo" },
  { title: "Acción", key: "accion" },
];

const formatFecha = value => {
  if (!value) return "—";
  return new Date(value).toLocaleString("es-ES");
};

const formatImporte = value => {
  if (value === null || value === undefined) return "—";
  return Number(value).toLocaleString("es-ES", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const loadEmpresas = async () => {
  try {
    const res = await $api("/list-sociedades", { method: "GET" });
    empresas.value = res ?? [];
  } catch {
    empresas.value = [];
  }
};

const fetchImportaciones = async () => {
  if (!selectedEmpresa.value) {
    importaciones.value = [];
    totalImportaciones.value = 0;
    return;
  }

  loading.value = true;
  try {
    const res = await $api("/cuentas-contables/importaciones", {
      method: "GET",
      params: {
        id_empresa: selectedEmpresa.value,
        anyo: anyo.value || undefined,
        numero_cuenta: numeroCuenta.value || undefined,
        itemsPerPage: itemsPerPage.value,
        page: page.value,
      },
    });
    importaciones.value = res.data ?? [];
    totalImportaciones.value = res.total ?? 0;
  } catch (e) {
    console.error(e);
    importaciones.value = [];
    totalImportaciones.value = 0;
  } finally {
    loading.value = false;
  }
};

const fetchLineas = async () => {
  if (!selectedImportacion.value?.id) return;

  loadingLineas.value = true;
  try {
    const res = await $api(
      `/cuentas-contables/importaciones/${selectedImportacion.value.id}/lineas`,
      {
        method: "GET",
        params: {
          numero_cuenta: numeroCuenta.value || undefined,
          itemsPerPage: lineasPerPage.value,
          page: lineasPage.value,
        },
      }
    );
    selectedImportacion.value = res.importacion ?? selectedImportacion.value;
    lineas.value = res.lineas?.data ?? [];
    totalLineas.value = res.lineas?.total ?? 0;
  } catch (e) {
    console.error(e);
    lineas.value = [];
    totalLineas.value = 0;
  } finally {
    loadingLineas.value = false;
  }
};

const openDetalle = item => {
  selectedImportacion.value = item;
  lineasPage.value = 1;
  detailDialog.value = true;
  fetchLineas();
};

const downloadArchivo = async id => {
  try {
    const blob = await $api(`/cuentas-contables/importaciones/${id}/archivo`, {
      responseType: "blob",
    });
    const row = importaciones.value.find(i => i.id === id);
    const nombre = row?.archivo_nombre || `importacion-${id}.xlsx`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = nombre;
    a.click();
    URL.revokeObjectURL(url);
  } catch (e) {
    console.error(e);
  }
};

const updateOptions = options => {
  page.value = options.page;
};

const updateLineasOptions = options => {
  lineasPage.value = options.page;
  fetchLineas();
};

const buscar = () => {
  page.value = 1;
  fetchImportaciones();
};

watch([itemsPerPage, page], () => {
  if (selectedEmpresa.value) fetchImportaciones();
});

watch([lineasPerPage, lineasPage], () => {
  if (detailDialog.value) fetchLineas();
});

onMounted(async () => {
  if (user.value?.idRol !== 1) {
    router.replace("/apps/company/list");
    return;
  }
  await loadEmpresas();
});
</script>

<template>
  <section v-if="user?.idRol === 1">
    <VCard class="mb-6">
      <VCardTitle class="d-flex align-center gap-2">
        <VIcon icon="ri-file-search-line" />
        Auditoría de importaciones Excel
      </VCardTitle>
      <VCardText>
        <p class="text-body-2 mb-4">
          Consulta qué usuario importó cada Excel, qué filas modificaron los importes en
          cuentas contables y descarga el archivo original.
        </p>

        <VRow>
          <VCol cols="12" md="4">
            <VSelect
              v-model="selectedEmpresa"
              :items="empresas"
              item-title="nombre"
              item-value="id"
              label="Empresa"
              placeholder="Seleccione empresa"
              clearable
              density="compact"
            />
          </VCol>
          <VCol cols="12" md="3">
            <VTextField
              v-model="numeroCuenta"
              label="Nº cuenta contable"
              placeholder="Ej. 43000001"
              density="compact"
              clearable
              @keyup.enter="buscar"
            />
          </VCol>
          <VCol cols="12" md="2">
            <VTextField
              v-model="anyo"
              label="Año"
              type="number"
              density="compact"
              clearable
              @keyup.enter="buscar"
            />
          </VCol>
          <VCol cols="12" md="3" class="d-flex align-center">
            <VBtn color="primary" :disabled="!selectedEmpresa" @click="buscar">
              Buscar
            </VBtn>
          </VCol>
        </VRow>
      </VCardText>
    </VCard>

    <VCard>
      <VDataTableServer
        v-model:items-per-page="itemsPerPage"
        v-model:page="page"
        :items="importaciones"
        :headers="headers"
        :items-length="totalImportaciones"
        :loading="loading"
        item-value="id"
        class="text-no-wrap"
        @update:options="updateOptions"
      >
        <template #item.created_at="{ item }">
          {{ formatFecha(item.created_at) }}
        </template>

        <template #item.usuario.name="{ item }">
          {{ item.usuario?.name ?? "—" }}
          <span
            v-if="item.usuario?.email"
            class="text-caption text-medium-emphasis d-block"
          >
            {{ item.usuario.email }}
          </span>
        </template>

        <template #item.tipo="{ item }">
          <VChip size="small" variant="tonal">
            {{ item.tipo === "inicial" ? "Subida inicial" : "Importación" }}
          </VChip>
        </template>

        <template #item.archivo_nombre="{ item }">
          <span class="text-truncate d-inline-block" style="max-width: 12rem">
            {{ item.archivo_nombre ?? "—" }}
          </span>
        </template>

        <template #item.actions="{ item }">
          <IconBtn size="small" title="Ver líneas" @click="openDetalle(item)">
            <VIcon icon="ri-list-unordered" />
          </IconBtn>
          <IconBtn
            v-if="item.archivo_ruta"
            size="small"
            title="Descargar Excel"
            @click="downloadArchivo(item.id)"
          >
            <VIcon icon="ri-download-line" />
          </IconBtn>
        </template>

        <template #bottom>
          <VDivider />
          <div class="d-flex justify-end flex-wrap gap-x-6 px-2 py-1">
            <div class="d-flex align-center gap-x-2 text-medium-emphasis text-base">
              Filas por página:
              <VSelect
                v-model="itemsPerPage"
                class="per-page-select"
                variant="plain"
                :items="[10, 15, 25, 50]"
              />
            </div>
            <p class="d-flex align-center text-base text-high-emphasis me-2 mb-0">
              {{ paginationMeta({ page, itemsPerPage }, totalImportaciones) }}
            </p>
            <div class="d-flex gap-x-2 align-center me-2">
              <VBtn
                icon="ri-arrow-left-s-line"
                variant="text"
                density="comfortable"
                :disabled="page <= 1"
                @click="page > 1 && page--"
              />
              <VBtn
                icon="ri-arrow-right-s-line"
                variant="text"
                density="comfortable"
                :disabled="page >= Math.ceil(totalImportaciones / itemsPerPage)"
                @click="
                  page < Math.ceil(totalImportaciones / itemsPerPage) && page++
                "
              />
            </div>
          </div>
        </template>
      </VDataTableServer>
    </VCard>

    <VDialog v-model="detailDialog" max-width="1100" scrollable>
      <VCard v-if="selectedImportacion">
        <VCardTitle class="d-flex align-center justify-space-between">
          <span>Detalle importación #{{ selectedImportacion.id }}</span>
          <DialogCloseBtn @click="detailDialog = false" />
        </VCardTitle>
        <VCardText>
          <VRow class="mb-4">
            <VCol cols="12" sm="6" md="3">
              <div class="text-caption text-medium-emphasis">Fecha</div>
              <div>{{ formatFecha(selectedImportacion.created_at) }}</div>
            </VCol>
            <VCol cols="12" sm="6" md="3">
              <div class="text-caption text-medium-emphasis">Usuario</div>
              <div>{{ selectedImportacion.usuario?.name ?? "—" }}</div>
            </VCol>
            <VCol cols="12" sm="6" md="2">
              <div class="text-caption text-medium-emphasis">Año</div>
              <div>{{ selectedImportacion.anyo }}</div>
            </VCol>
            <VCol cols="12" sm="6" md="2">
              <div class="text-caption text-medium-emphasis">Columna</div>
              <div>{{ selectedImportacion.columna_total ?? "—" }}</div>
            </VCol>
            <VCol cols="12" sm="6" md="2" class="d-flex align-end">
              <VBtn
                v-if="selectedImportacion.archivo_ruta"
                size="small"
                variant="tonal"
                prepend-icon="ri-download-line"
                @click="downloadArchivo(selectedImportacion.id)"
              >
                Excel
              </VBtn>
            </VCol>
          </VRow>

          <VDataTableServer
            v-model:items-per-page="lineasPerPage"
            v-model:page="lineasPage"
            :items="lineas"
            :headers="lineasHeaders"
            :items-length="totalLineas"
            :loading="loadingLineas"
            item-value="id"
            density="compact"
            @update:options="updateLineasOptions"
          >
            <template #item.importe_excel="{ item }">
              {{ formatImporte(item.importe_excel) }}
            </template>
            <template #item.valor_anterior="{ item }">
              {{ formatImporte(item.valor_anterior) }}
            </template>
            <template #item.valor_nuevo="{ item }">
              {{ formatImporte(item.valor_nuevo) }}
            </template>
            <template #item.accion="{ item }">
              <VChip
                size="x-small"
                :color="item.accion === 'create' ? 'success' : 'info'"
              >
                {{ item.accion === "create" ? "Alta" : "Actualización" }}
              </VChip>
            </template>
          </VDataTableServer>
        </VCardText>
      </VCard>
    </VDialog>
  </section>
</template>
